import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, fullName, formatDate, formatEUR } from '../lib/api';
import { telefonoConPrefijo } from '../lib/utils';
import { PageHeader, StatusBadge, SectionCard, EmptyState } from '../components/Common';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Eye, FileText, ShieldCheck, Stethoscope, FilePen, Receipt, ListChecks, AlertCircle, Wallet, CreditCard, Building2, Link2, ReceiptText, BedDouble, Megaphone, CheckCircle2, FileSignature, HeartPulse, CalendarPlus, Lock, Images, Pencil, Pill, Package, MessageCircle, Trash2, Combine } from 'lucide-react';
import PdfPreviewDialog from '../components/PdfPreviewDialog';
import PatientRecetasTab from '../components/patient/PatientRecetasTab';
import PatientPhotosTab from '../components/patient/PatientPhotosTab';
import PruebasPreopCard from '../components/patient/PruebasPreopCard';
import PdfLangButton from '../components/PdfLangButton';
import WhatsAppPaqueteButton from '../components/WhatsAppPaqueteButton';
import HistoriaClinicaTab from '../components/patient/HistoriaClinicaTab';
import NotasInternasTab from '../components/patient/NotasInternasTab';
import InformesPacienteTab from '../components/patient/InformesPacienteTab';
// El envío de consentimientos a la tablet se hace DESDE AQUÍ
// (18/9/2026): la pestaña entera vive en su propio componente porque ya no
// es una lista, sino la lista + la cola del iPad.
import PatientConsentsTab from '../components/patient/PatientConsentsTab';
import NotasClinicas from '../components/patient/NotasClinicas';
import PatientFormDialog from '../components/PatientFormDialog';
import PostopQuickDialog from '../components/quirofano/PostopQuickDialog';
import NewBudgetDialog from '../components/NewBudgetDialog';
import DarCitaDialog from '../components/citas/DarCitaDialog';
import CobroQuickDialog from '../components/CobroQuickDialog';
import EditarCobroDialog from '../components/EditarCobroDialog';
import { desgloseGuardado, esIvaIncluido } from '../lib/iva';
import { useAuth } from '../lib/auth';
import { canSeeInternalNotes, isDoctor, isManagement, isGestion } from '../lib/roles';
import { estadoFunnel } from '../lib/funnel';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ---------------------------------------------------------------------------
// Cómo se llama a un presupuesto (2026-09-15, punto 5; revisión del
// mismo día)
// ---------------------------------------------------------------------------
// Por SU número, PRES-AAAA-NNNN, y por ningún otro.
//
// Antes la ficha le ponía encima un ordinal —«Presupuesto 1», «Presupuesto
// 2»— contando los que quedaban en la lista. Eso aguanta hasta que se borra
// uno: borras el 1 y el que era 2 amanece siendo el 1. Dirección dice por
// teléfono «mándale el presupuesto 2» y sale otro, con otro importe. Un
// número que se mueve según cuántos queden no sirve para nombrar nada.
//
// El PRES-AAAA-NNNN lo da un contador atómico del servidor: no se reutiliza,
// no se mueve porque se borre otro y es EL MISMO que va impreso en el PDF que
// recibe la paciente («Nº de presupuesto»). Así la fila de la pantalla, el
// botón que lo manda y el papel que ella tiene delante dicen los tres lo
// mismo, y ya no hay nada que contar.

/**
 * Caducado = su validez ya pasó, lo diga o no la etiqueta de estado.
 *
 * La ficha lee los presupuestos por /api/dashboard/patient/{id}, que NO
 * recalcula el estado (eso solo lo hace la pantalla de Presupuestos): uno
 * enviado en enero con validez hasta marzo sigue poniendo «Enviado» en
 * septiembre. Por eso aquí manda la FECHA; el `status` solo suma cuando el
 * servidor ya lo marcó. El último día válido es el propio `valid_until`,
 * igual que cuenta el backend.
 */
function presupuestoCaducado(b) {
  if (!b) return false;
  if (b.status === 'expirado') return true;
  const hasta = String(b.valid_until || '').slice(0, 10);
  if (!hasta) return false;
  const d = new Date();
  const hoy = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return hasta < hoy;
}

// ---------------------------------------------------------------------------
// «FUSIONES DE ESTA FICHA» (supervisión, 2026-09-18)
// ---------------------------------------------------------------------------
// El diálogo de fusionar promete por escrito que lo que no se aplica «no se
// borra: queda en el archivo de la fusión». El archivo existía —lo guarda
// `fusiones_pacientes` y lo sirve `GET /api/patients/{id}/fusiones` desde el
// 2026-09-17— pero no lo abría ninguna pantalla. Una fusión no se puede
// deshacer: si además nadie puede mirar qué se quedó fuera, el «no se pierde
// nada» era una promesa que no se podía comprobar.
//
// Solo dirección. Dentro va la ficha absorbida entera y sus notas: ahí está lo
// que debe el paciente y lo que otro médico escribió en privado.
function ListaCamposFusion({ titulo, campos, texto, testid, clase }) {
  if (!campos || campos.length === 0) return null;
  return (
    <div className="mt-1.5" data-testid={testid}>
      <div className={`text-[11px] font-semibold uppercase tracking-wide ${clase}`}>{titulo}</div>
      <ul className="mt-0.5 ml-4 list-disc text-xs">
        {campos.map(c => (
          <li key={c.campo}><strong>{c.etiqueta || c.campo}:</strong> {texto(c)}</li>
        ))}
      </ul>
    </div>
  );
}

// UN ERROR NO SE ENSEÑA COMO UN DATO (supervisión, 2026-09-21)
// ---------------------------------------------------------------------------
// Hasta hoy esto era `.catch(() => setFusiones([]))` y un bloque que no se
// pinta con la lista vacía: un 500, un corte de red o la sesión caducada a
// media página se veían EXACTAMENTE igual que «esta ficha no se ha fusionado
// nunca». Y aquí callarse es más caro que en otras pantallas, porque no hay
// segunda fuente: el archivo no se puede pedir por ningún otro sitio y la
// fusión no se deshace. Dirección abría la ficha para comprobar qué se
// descartó —un correo, un consentimiento, la nota con lo que la paciente
// debe— y la pantalla le contestaba, en silencio, que ahí no había nada.
//
// El 403 es el ÚNICO silencio que se queda: al resto de perfiles el servidor
// les contesta 403 a propósito, y ahí no hay nada roto que contar.
function AvisoFusionesIlegible({ onReintentar }) {
  return (
    <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-3" data-testid="patient-fusiones-error">
      <div className="text-sm font-semibold text-red-800 flex items-center gap-2">
        <AlertCircle className="w-4 h-4" /> No se ha podido leer el archivo de fusiones de esta ficha
      </div>
      <div className="text-xs text-red-800 mt-0.5">
        Esto NO quiere decir que la ficha no se haya fusionado: quiere decir que ahora mismo no se
        sabe. Si necesitas comprobar qué se descartó al fusionar, vuelve a intentarlo antes de dar
        nada por bueno.
      </div>
      <Button
        variant="outline"
        size="sm"
        className="mt-2 h-7 text-xs"
        onClick={onReintentar}
        data-testid="patient-fusiones-reintentar"
      >
        Reintentar
      </Button>
    </div>
  );
}

function BloqueFusiones({ items, estado, onReintentar }) {
  // Primero lo que no se sabe: un error no se pinta como una lista vacía.
  if (estado === 'error') return <AvisoFusionesIlegible onReintentar={onReintentar} />;
  // Sin fusiones no se pinta nada: la ficha normal no necesita un cartel que
  // diga que no ha pasado nada.
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="patient-fusiones">
      <div className="text-sm font-semibold text-amber-900 flex items-center gap-2">
        <Combine className="w-4 h-4" /> Fusiones de esta ficha ({items.length})
      </div>
      <div className="text-xs text-amber-900 mt-0.5">
        Esta ficha absorbió otra(s). Aquí está quién lo hizo, cuándo, qué ficha se fundió
        y qué dato suyo no se aplicó. Nada de eso se ha borrado: se guarda entero.
      </div>
      {items.map(f => (
        <details key={f.id} className="mt-2 rounded-md border border-amber-300 bg-white p-2" data-testid="patient-fusion-item">
          <summary className="text-xs cursor-pointer text-[#13231E]">
            <strong>{f.nombre || '(sin nombre)'}</strong>
            {f.num_historia !== null && f.num_historia !== undefined ? ` · nº ${f.num_historia}` : ''}
            {f.nif ? ` · NIF ${f.nif}` : ''}
            {' — la fusionó '}
            <strong>{f.fusionado_por_nombre || '—'}</strong>
            {' el '}{formatDate(f.fusionado_at)}
          </summary>
          <ListaCamposFusion
            titulo="Alergias y antecedentes que quedaron apuntados"
            campos={f.campos_marcados}
            testid="patient-fusion-marcados"
            clase="text-amber-900"
            texto={(c) => `queda «${c.resultado}» — lo tenía apuntado la ficha fusionada`}
          />
          <ListaCamposFusion
            titulo="Consentimientos que se apretaron"
            campos={f.campos_restringidos}
            testid="patient-fusion-restringidos"
            clase="text-red-800"
            texto={(c) => `queda «${c.resultado}» — la ficha fusionada decía que no`}
          />
          <ListaCamposFusion
            titulo="Texto clínico que se añadió debajo"
            campos={f.campos_pegados}
            testid="patient-fusion-pegados"
            clase="text-emerald-800"
            texto={(c) => `se añadió «${c.descartada}»`}
          />
          {/* LA NEGACIÓN QUE EL RIESGO SUSTITUYÓ (2026-09-21).
              Es LO ÚNICO de la fusión que deja de verse en la ficha viva: el
              resto o se pega o se queda. Cuando la principal tenía escrito
              «Sin alergias conocidas (nota quirófano …)» y la otra «LATEX», el
              látex no se pega tres líneas más abajo de una frase que dice lo
              contrario: ocupa su sitio. Lo que se quitó era una negación, no
              un dato del paciente — pero eso lo tiene que poder comprobar
              dirección aquí, no creérselo. */}
          <ListaCamposFusion
            titulo="Negaciones que el riesgo apuntado sustituyó"
            campos={f.campos_sustituidos}
            testid="patient-fusion-sustituidos"
            clase="text-amber-900"
            texto={(c) => `queda «${c.resultado}» — la ficha decía «${c.principal}», que no apunta ningún riesgo`}
          />
          <ListaCamposFusion
            titulo="Lo que NO se aplicó (mandó esta ficha)"
            campos={f.campos_descartados}
            testid="patient-fusion-descartados"
            clase="text-amber-800"
            texto={(c) => `quedó «${c.resultado}», se descartó «${c.descartada}»`}
          />
          {Object.keys(f.referencias_movidas || {}).length > 0 && (
            <div className="mt-1.5 text-xs text-muted-foreground" data-testid="patient-fusion-referencias">
              Se movieron a esta ficha:{' '}
              {Object.entries(f.referencias_movidas).map(([k, v]) => `${v} ${k}`).join(' · ')}
            </div>
          )}
        </details>
      ))}
    </div>
  );
}

function PaymentMethodIcon({ method }) {
  if (method === 'Efectivo') return <Wallet className="w-3.5 h-3.5" />;
  if (method === 'Tarjeta') return <CreditCard className="w-3.5 h-3.5" />;
  if (method === 'Transferencia') return <Building2 className="w-3.5 h-3.5" />;
  if (method === 'Enlace de pago') return <Link2 className="w-3.5 h-3.5" />;
  return <ReceiptText className="w-3.5 h-3.5" />;
}

export default function PatientDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const showInternalNotes = canSeeInternalNotes(user);
  // Los doctores ven la ficha en versión clínica: sin resumen económico ni
  // pestañas administrativas (presupuestos, LOPD, cobros).
  const doctor = isDoctor(user);
  // Los cobros —pestaña, resumen económico, registrar, corregir y borrar— son
  // de GESTIÓN y de nadie más (D2, 22/9/2026: «enfermería no
  // necesita cobros»). Antes se escondían solo a doctor y anestesista; desde
  // que el servidor le sirve a enfermería la lista vacía, ella leía «Cobros
  // (0)» y «Sin cobros»: lo que no sabe, pintado como el dato de un paciente
  // que no ha pagado nunca. Y su «Registrar cobro» acababa en un 403.
  const gestion = isGestion(user);
  // Dirección (y solo dirección) puede abrir el archivo de las fusiones: ahí
  // dentro está la ficha absorbida entera y sus notas, que incluyen lo que
  // debe el paciente y lo que otro médico escribió en privado.
  const direccion = isManagement(user);
  const [data, setData] = useState(null);
  // EL ARCHIVO DE LA FUSIÓN, A LA VISTA (supervisión, 2026-09-18).
  // `GET /api/patients/{id}/fusiones` existía desde el 2026-09-17 y funcionaba,
  // pero no lo llamaba NINGUNA pantalla: la promesa que firma el diálogo de
  // fusionar —«no se borra nada, queda guardado»— no la podía comprobar nadie
  // desde la aplicación. Una promesa que no se puede abrir no es un archivo,
  // es un cajón cerrado.
  const [fusiones, setFusiones] = useState([]);
  // …Y SI NO SE PUEDE LEER, SE DICE (supervisión, 2026-09-21).
  // Tres estados, porque en pantalla NO significan lo mismo: «ok» (lo tengo,
  // aunque venga vacío), «error» (no se ha podido leer: se pinta en rojo) y
  // «sin-permiso» (403, o no es dirección: se calla, que ahí no hay nada
  // roto). Se arranca en «sin-permiso» a propósito: mientras la petición está
  // en el aire no se sabe nada, y lo que no se sabe no se pinta. El contador
  // de intentos es lo que vuelve a disparar el efecto al pulsar «Reintentar».
  const [fusionesEstado, setFusionesEstado] = useState('sin-permiso');
  const [fusionesIntento, setFusionesIntento] = useState(0);
  const [summary, setSummary] = useState(null);
  const [postopDocs, setPostopDocs] = useState([]);
  const [anesthesiaSheets, setAnesthesiaSheets] = useState([]);
  const [informes, setInformes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState({ open: false, url: '', filename: '', title: '' });
  // Phase 27 — Historia clínica + Dar cita desde la ficha del paciente.
  const [darCitaOpen, setDarCitaOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editBudget, setEditBudget] = useState(null);
  // Presupuestos caducados que alguien ha decidido mandar de todas formas.
  // Se olvida al salir de la ficha: la decisión vale para este rato, no para
  // siempre, porque mañana el precio estará un día más vencido.
  const [caducadosDesbloqueados, setCaducadosDesbloqueados] = useState([]);
  const desbloquearEnvioCaducado = (b) => {
    // El clic de más (2026-09-15): leer no es enviar, y enviar un precio
    // vencido tiene que ser una decisión, no un descuido.
    const aviso = `El presupuesto ${b.number} caducó el ${formatDate(b.valid_until)}.\n\n`
      + 'Si lo mandas, la paciente lo recibe con la validez vencida bajo el texto '
      + '«Le hacemos llegar su presupuesto». Lo normal es editarlo y renovar la '
      + 'validez antes de enviarlo.\n\n¿Mandarlo igual, caducado?';
    if (!window.confirm(aviso)) return;
    setCaducadosDesbloqueados((ids) => (ids.includes(b.id) ? ids : [...ids, b.id]));
  };
  const [darPostopOpen, setDarPostopOpen] = useState(false);
  const [cobroOpen, setCobroOpen] = useState(false);
  // Corregir o borrar un cobro desde la ficha (2026-08-31).
  const [editCobro, setEditCobro] = useState(null);
  const borrarCobro = async (c) => {
    if (!window.confirm(`¿Borrar el cobro «${c.concept}» de ${formatEUR(c.total || c.amount || 0)}? Queda registrado quién lo borra.`)) return;
    try {
      await api.delete(`/api/cobros/${c.id}`);
      toast.success('Cobro borrado');
      load(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo borrar el cobro');
    }
  };
  const [citasRefreshKey, setCitasRefreshKey] = useState(0);

  const load = React.useCallback((withSpinner = true) => {
    if (withSpinner) setLoading(true);
    return Promise.all([
      api.get(`/api/dashboard/patient/${id}`),
      // El resumen económico es de gestión (D2, 22/9/2026): al resto el
      // servidor le contesta 403, así que ni se le pide.
      gestion
        ? api.get(`/api/cobros/patient/${id}/summary`).catch(() => ({ data: null }))
        : Promise.resolve({ data: null }),
      api.get(`/api/postoperatorios/documents`, { params: { patient_id: id } }).catch(() => ({ data: [] })),
      // Phase 25.F — hojas de anestesia del paciente para que aparezcan
      // en el gestor documental del paciente (no solo en Registro QX).
      api.get(`/api/anesthesia-sheets`, { params: { patient_id: id } }).catch(() => ({ data: [] })),
      // Phase 26.5 — Informes médicos del paciente.
      api.get(`/api/informes`, { params: { patient_id: id } }).catch(() => ({ data: [] })),
    ]).then(([d, s, po, as_, inf]) => {
      setData(d.data);
      setSummary(s.data);
      setPostopDocs(po.data || []);
      setAnesthesiaSheets(as_.data || []);
      setInformes(inf.data || []);
    }).finally(() => { if (withSpinner) setLoading(false); });
  }, [id, gestion]);

  useEffect(() => { load(true); }, [load]);

  // Va aparte de `load` a propósito: al resto de perfiles el servidor les
  // contesta 403, y no se le pide a quien no puede verlo.
  useEffect(() => {
    if (!direccion) { setFusiones([]); setFusionesEstado('sin-permiso'); return; }
    let vivo = true;
    api.get(`/api/patients/${id}/fusiones`)
      .then(r => { if (vivo) { setFusiones(r.data?.items || []); setFusionesEstado('ok'); } })
      .catch(e => {
        if (!vivo) return;
        setFusiones([]);
        // El 403 se calla: el servidor lo contesta a propósito a quien no es
        // dirección. Todo lo demás —500, corte de red, sesión caducada— se
        // dice, que si no «no se ha podido leer» se lee como «no hay ninguna».
        setFusionesEstado(e?.response?.status === 403 ? 'sin-permiso' : 'error');
      });
    return () => { vivo = false; };
  }, [id, direccion, fusionesIntento]);

  if (loading) return <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>;
  if (!data) return <div className="py-10 text-center text-sm text-muted-foreground">Paciente no encontrado</div>;
  const p = data.patient;
  const openPreview = (url, filename, title) => setPreview({ open: true, url, filename, title });
  const hasPending = summary?.has_pending;
  const funnel = estadoFunnel(p);
  // Consentimiento de imágenes para redes: firmado si existe el doc de la
  // plantilla fotos-redes con la firma del paciente.
  const consentImagenesFirmado = (data.consents || []).some(
    (c) => c.template_code === 'fotos-redes' && (c.patient_signature_b64 || '').trim(),
  );
  const puedeMarcarFotos = doctor || isManagement(user);

  const cambiarPermiteFotos = async (permite) => {
    try {
      const res = await api.put(`/api/patients/${id}/permite-fotos`, { permite });
      if (res.data?.consentimiento_creado) {
        toast.success('Consentimiento de imágenes generado — envíalo a la tablet para que lo firme');
      }
      load(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    }
  };

  return (
    <div>
      <Link to="/pacientes" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft className="w-4 h-4 mr-1" /> Volver
      </Link>
      <PageHeader
        title={fullName(p) || 'Paciente'}
        subtitle={`${p.num_historia ? `Nº Historia: ${p.num_historia} · ` : ''}NIF: ${p.nif || '—'} · Tel: ${telefonoConPrefijo(p.phone) || '—'} · Email: ${p.email || '—'}`}
        actions={
          <>
            {funnel && (
              <Badge className={`${funnel.cls} border`} data-testid="patient-funnel-badge">
                {funnel.label}
              </Badge>
            )}
            {p.consent_commercial && <Badge variant="secondary" className="bg-[#2E6E5E]/15 text-[#13231E] border-[#2E6E5E]/30"><Megaphone className="w-3 h-3 mr-1" /> Marketing OK</Badge>}
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="patient-editar">
              <Pencil className="w-4 h-4 mr-2" /> Editar
            </Button>
            <Button onClick={() => setDarCitaOpen(true)} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="patient-dar-cita">
              <CalendarPlus className="w-4 h-4 mr-2" /> Dar cita
            </Button>
          </>
        }
      />

      {/* Fotos para redes sociales: lo marca el médico. Con el Sí, el sistema
          genera el consentimiento de imágenes y avisa hasta que esté firmado. */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="permite-fotos-strip">
        <Images className="w-4 h-4 text-[#2E6E5E]" />
        <span className="text-sm font-medium text-[#13231E]">Fotos para redes:</span>
        <div className="flex rounded-md border border-border overflow-hidden">
          <button
            type="button"
            disabled={!puedeMarcarFotos}
            onClick={() => cambiarPermiteFotos(p.permite_fotos === true ? null : true)}
            className={`px-3 py-1 text-xs font-semibold transition-colors ${p.permite_fotos === true ? 'bg-emerald-600 text-white' : 'bg-transparent text-muted-foreground hover:bg-muted'} ${!puedeMarcarFotos ? 'opacity-60 cursor-not-allowed' : ''}`}
            data-testid="permite-fotos-si"
          >
            Sí
          </button>
          <button
            type="button"
            disabled={!puedeMarcarFotos}
            onClick={() => cambiarPermiteFotos(p.permite_fotos === false ? null : false)}
            className={`px-3 py-1 text-xs font-semibold border-l border-border transition-colors ${p.permite_fotos === false ? 'bg-red-600 text-white' : 'bg-transparent text-muted-foreground hover:bg-muted'} ${!puedeMarcarFotos ? 'opacity-60 cursor-not-allowed' : ''}`}
            data-testid="permite-fotos-no"
          >
            No
          </button>
        </div>
        {p.permite_fotos === true && !consentImagenesFirmado && (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 bg-red-50 dark:bg-red-950/30 border border-red-200 rounded px-2 py-1" data-testid="aviso-consent-imagenes">
            <AlertCircle className="w-3.5 h-3.5" />
            Debe firmar el consentimiento de imágenes — ya está generado, envíalo a la tablet
          </span>
        )}
        {p.permite_fotos === true && consentImagenesFirmado && (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700" data-testid="consent-imagenes-ok">
            <CheckCircle2 className="w-3.5 h-3.5" /> Consentimiento de imágenes firmado
          </span>
        )}
        {p.permite_fotos_by && p.permite_fotos != null && (
          <span className="text-[11px] text-muted-foreground">marcado por {p.permite_fotos_by}</span>
        )}
      </div>

      <Card className="cp-card p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div><span className="text-xs uppercase tracking-wider text-muted-foreground">Fecha nacimiento</span><div className="font-medium">{p.birth_date || '—'}</div></div>
          <div><span className="text-xs uppercase tracking-wider text-muted-foreground">Profesión</span><div className="font-medium">{p.profession || '—'}</div></div>
          <div><span className="text-xs uppercase tracking-wider text-muted-foreground">Cómo nos conoció</span><div className="font-medium">{p.how_known_us || '—'}</div></div>
          <div className="md:col-span-3">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Dirección</span>
            <div className="font-medium">{[p.address?.calle, p.address?.poblacion, p.address?.provincia, p.address?.codigo_postal, p.address?.pais].filter(Boolean).join(', ') || '—'}</div>
          </div>
        </div>
      </Card>

      {/* Resumen económico — Phase 17.3. Solo gestión (D2, 22/9/2026): al
          resto ni se le pide (ver `load`). */}
      {gestion && summary && (
        <Card
          data-testid="patient-payment-summary"
          className={`cp-card p-4 mb-6 ${hasPending ? 'border-l-4 border-l-red-600 bg-red-50/40' : 'border-l-4 border-l-green-500 bg-green-50/40'}`}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className={`rounded-full p-2 ${hasPending ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                {hasPending ? <AlertCircle className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
              </div>
              <div>
                <div className="font-display font-semibold text-[#13231E]">
                  {hasPending ? 'Saldo pendiente' : (summary.total_reference > 0 ? 'Pago completo' : 'Sin presupuesto')}
                </div>
                {summary.budget_number && (
                  <div className="text-xs text-muted-foreground">Presupuesto activo: <strong>{summary.budget_number}</strong></div>
                )}
                {/* Servicios APARTE del presupuesto (p. ej. un tratamiento suelto):
                    cuentan en la referencia y en lo cobrado, y aquí se desglosa
                    cada uno con su estado. Una hecha y SIN COBRAR sale en rojo:
                    es deuda que antes no se veía (2026-08-19). */}
                {summary.aparte?.length > 0 && (
                  <div className="text-xs text-muted-foreground mt-0.5" data-testid="saldo-aparte">
                    Incluye, aparte del presupuesto{summary.presupuesto_total > 0 ? ` (${formatEUR(summary.presupuesto_total)})` : ''}:{' '}
                    {summary.aparte.map((a, i) => (
                      <span key={i}>
                        {i > 0 ? ' · ' : ''}
                        <strong className={a.cobrado ? 'text-green-700' : 'text-red-700'}>
                          {a.concepto}{a.importe ? ` ${formatEUR(a.importe)}` : ''} {a.cobrado ? '(cobrada)' : '(SIN COBRAR)'}
                        </strong>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-3 text-sm">
              <div className="bg-white border border-border px-3 py-1.5 rounded">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Referencia</div>
                <div className="font-semibold tabular-nums">{formatEUR(summary.total_reference)}</div>
              </div>
              <div className="bg-white border border-border px-3 py-1.5 rounded">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Cobrado</div>
                <div className="font-semibold text-green-700 tabular-nums">{formatEUR(summary.paid_total)}</div>
              </div>
              <div className={`px-3 py-1.5 rounded ${hasPending ? 'bg-red-100 border border-red-300' : 'bg-white border border-border'}`}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Pendiente</div>
                <div className={`font-bold tabular-nums ${hasPending ? 'text-red-700' : 'text-muted-foreground'}`}>
                  {formatEUR(Math.max(0, summary.pending))}
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* El archivo de las fusiones de esta ficha. Solo dirección; vacío,
          no se pinta; ilegible, se dice en rojo (2026-09-18 / 2026-09-21) */}
      <BloqueFusiones
        items={fusiones}
        estado={fusionesEstado}
        onReintentar={() => setFusionesIntento(n => n + 1)}
      />

      {/* Phase 27 — Banner de alergias, siempre visible encima de las tabs */}
      {p.alergias === true && (
        <div
          className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-red-900 font-semibold flex items-start gap-2"
          data-testid="patient-alergias-banner"
        >
          <span aria-hidden="true">⚠</span>
          <span>ALERGIAS{p.alergias_text ? `: ${p.alergias_text}` : ''}</span>
        </div>
      )}

      <Tabs defaultValue="historia">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="historia" data-testid="historia-tab"><HeartPulse className="w-4 h-4 mr-1" /> Historia clínica</TabsTrigger>
          {showInternalNotes && (
            <TabsTrigger value="notas-internas" data-testid="notas-internas-tab">
              <Lock className="w-4 h-4 mr-1" /> Notas de administración
            </TabsTrigger>
          )}
          {/* Presupuestos: SOLO administración. El importe es confidencial
              (2026-08-18). Antes lo veía también enfermería. */}
          {gestion && <TabsTrigger value="presupuestos"><FileText className="w-4 h-4 mr-1" /> Presupuestos ({data.budgets.length})</TabsTrigger>}
          {!doctor && <TabsTrigger value="lopd"><ShieldCheck className="w-4 h-4 mr-1" /> LOPD ({data.lopd.length})</TabsTrigger>}
          {/* «Pruebas preoperatorias» (antes «Volantes preop.», 2026-09-03):
              aquí llega la analítica que el paciente sube desde su enlace. */}
          <TabsTrigger value="preop" data-testid="patient-tab-preop"><Stethoscope className="w-4 h-4 mr-1" /> Pruebas preoperatorias</TabsTrigger>
          <TabsTrigger value="instructions"><ListChecks className="w-4 h-4 mr-1" /> Indicaciones ({(data.instructions || []).length})</TabsTrigger>
          <TabsTrigger value="consents"><FilePen className="w-4 h-4 mr-1" /> Consentimientos ({data.consents.length})</TabsTrigger>
          <TabsTrigger value="postop"><BedDouble className="w-4 h-4 mr-1" /> Postoperatorios ({postopDocs.length})</TabsTrigger>
          <TabsTrigger value="recetas" data-testid="patient-tab-recetas"><Pill className="w-4 h-4 mr-1" /> Recetas</TabsTrigger>
          <TabsTrigger value="fotos" data-testid="patient-tab-fotos"><Images className="w-4 h-4 mr-1" /> Fotos</TabsTrigger>
          <TabsTrigger value="informes" data-testid="patient-tab-informes"><FileSignature className="w-4 h-4 mr-1" /> Informes ({informes.length})</TabsTrigger>
          {gestion && <TabsTrigger value="cobros" data-testid="patient-tab-cobros"><Receipt className="w-4 h-4 mr-1" /> Cobros ({data.cobros.length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="historia" className="mt-4">
          {/* Phase 27.20 — Notas clínicas: en la franja superior compacta del
              tab, junto a los datos clínicos, para que la historia quede a la vista */}
          <HistoriaClinicaTab
            patient={p}
            onPatientUpdated={() => load(false)}
            refreshKey={citasRefreshKey}
            notasSlot={<NotasClinicas patient={p} user={user} />}
          />
        </TabsContent>

        {showInternalNotes && (
          <TabsContent value="notas-internas" className="mt-4">
            <NotasInternasTab patient={p} />
          </TabsContent>
        )}

        <TabsContent value="fotos" className="mt-4">
          <PatientPhotosTab patient={p} />
        </TabsContent>

        <TabsContent value="presupuestos" className="mt-4">
          <SectionCard title="Presupuestos" action={<Link to={`/presupuestos?patient=${p.id}`} className="text-sm text-[#2E6E5E] hover:underline">+ Nuevo</Link>}>
            {data.budgets.length === 0 ? <EmptyState icon={FileText} title="Sin presupuestos" description="Genera uno desde aquí." /> : (
              <div className="divide-y divide-border">
                {data.budgets.map(b => {
                  const caducado = presupuestoCaducado(b);
                  const puedeEnviarse = !caducado || caducadosDesbloqueados.includes(b.id);
                  return (
                  <div key={b.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium" data-testid={`ficha-presupuesto-fila-${b.id}`}>
                        <span className="text-[#0e6f64]">Presupuesto {b.number}</span> · {b.surgery_name || 'Sin cirugía'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatDate(b.date)} · {formatEUR(b.total)} ·{' '}
                        {caducado
                          ? <span className="text-amber-800 font-semibold">caducó el {formatDate(b.valid_until)}</span>
                          : <>válido hasta {formatDate(b.valid_until)}</>}
                      </div>
                    </div>
                    {/* Si el servidor ya lo marcó «expirado», su etiqueta gris
                        dice lo mismo que la marca ámbar: sobra una. */}
                    {b.status !== 'expirado' && <StatusBadge status={b.status} />}
                    {caducado && (
                      <span
                        className="inline-flex items-center gap-1 shrink-0 px-2.5 py-0.5 rounded-full border text-xs font-semibold bg-amber-100 text-amber-900 border-amber-300"
                        data-testid={`ficha-presupuesto-caducado-${b.id}`}
                        title={`Su validez venció el ${formatDate(b.valid_until)}`}
                      >
                        <AlertCircle className="w-3 h-3" /> Caducado
                      </span>
                    )}
                    {/* El PRES- completo en el botón hace la fila más ancha:
                        que envuelva antes que salirse en una pantalla pequeña. */}
                    <div className="flex items-center justify-end gap-1 flex-wrap">
                      <PdfLangButton
                        baseUrl={`${BACKEND_URL}/api/budgets/${b.id}/pdf`}
                        filename={`presupuesto_${b.number}.pdf`}
                        title={`Presupuesto ${b.number} · ${b.patient_name}`}
                        onOpen={(x) => setPreview({ open: true, ...x })}
                      >
                        <Button size="sm" variant="outline" title="Ver presupuesto (elige idioma)"><Eye className="w-4 h-4" /></Button>
                      </PdfLangButton>
                      {(b.preop_id || b.instructions_id) && (
                        <PdfLangButton
                          baseUrl={`${BACKEND_URL}/api/budgets/${b.id}/bundle-pdf`}
                          filename={`paquete_${b.number}.pdf`}
                          title={`Paquete completo · ${b.patient_name}`}
                          onOpen={(x) => setPreview({ open: true, ...x })}
                        >
                          <Button size="sm" className="bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white" title="Paquete: presupuesto + preoperatorio (elige idioma)">
                            <Package className="w-4 h-4 mr-1" /> Paquete
                          </Button>
                        </PdfLangButton>
                      )}
                      {/* Mirar un presupuesto es gratis (los botones de arriba);
                          mandarlo caducado, no. Mientras está vencido, en el
                          sitio de los dos botones de WhatsApp hay uno solo que
                          dice lo que pasa y pregunta antes de dejar mandar
                          (2026-09-15). Lo sano es editarlo y renovar
                          la validez, que está aquí al lado. */}
                      {!puedeEnviarse ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100"
                          onClick={() => desbloquearEnvioCaducado(b)}
                          data-testid={`ficha-presupuesto-desbloquear-${b.id}`}
                          title={`Caducó el ${formatDate(b.valid_until)}: no se manda por descuido. Pulsa para decidirlo.`}
                        >
                          <AlertCircle className="w-4 h-4 mr-1" /> Mandar caducado
                        </Button>
                      ) : (
                        <>
                          {/* El paquete arrastra TODOS los presupuestos vivos
                              del paciente, y ninguno nace «borrador»: todos se
                              crean «pendiente». Por eso, antes de mandarlo,
                              enseña la lista de lo que va dentro y hay que
                              decir que sí (2026-09-15). Lo que no deba
                              salir —un tanteo a medio escribir— se desmarca
                              ahí. El diálogo lo pone el propio botón. */}
                          <WhatsAppPaqueteButton budget={b} onSent={() => load(false)} dataTestid="ficha-paquete-online">
                            <Button
                              size="sm"
                              className="bg-[#128C7E] hover:bg-[#0e6f64] text-white"
                              title="Paquete online: presupuestos + pruebas preoperatorias por WhatsApp. Eliges idioma y te enseña qué va dentro antes de mandarlo"
                            >
                              <MessageCircle className="w-4 h-4 mr-1" /> Paquete online
                            </Button>
                          </WhatsAppPaqueteButton>
                          {/* Suelto, al lado del paquete y con los mismos dos
                              idiomas (2026-09-15). Lleva escrito el
                              PRES- del presupuesto de ESTA fila: es el mismo que
                              encabeza la fila y el que va impreso en el papel,
                              así que quien oye un número por teléfono lo
                              encuentra sin contar filas. */}
                          <WhatsAppPaqueteButton
                            budget={b}
                            alcance="presupuesto"
                            onSent={() => load(false)}
                            dataTestid={`ficha-presupuesto-solo-${b.id}`}
                          >
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-[#128C7E] text-[#0e6f64] hover:bg-[rgba(18,140,126,0.10)]"
                              title={`Enviar SOLO este presupuesto (${b.number}) por WhatsApp, sin las pruebas preoperatorias (elige idioma)`}
                            >
                              <MessageCircle className="w-4 h-4 mr-1" /> Presupuesto {b.number}
                            </Button>
                          </WhatsAppPaqueteButton>
                        </>
                      )}
                      <Button size="sm" variant="outline" onClick={() => setEditBudget(b)} title="Editar cirugía e importes" data-testid="ficha-budget-edit">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="lopd" className="mt-4">
          <SectionCard title="LOPD" action={<Link to={`/lopd?patient=${p.id}`} className="text-sm text-[#2E6E5E] hover:underline">+ Generar</Link>}>
            {data.lopd.length === 0 ? <EmptyState icon={ShieldCheck} title="Sin LOPD" description="Genera el LOPD del paciente." /> : (
              <div className="divide-y divide-border">
                {data.lopd.map(l => (
                  <div key={l.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0"><div className="text-sm font-medium">Firmado: {formatDate(l.signed_date)}</div>
                      <div className="text-xs text-muted-foreground">Recordatorios: {l.consent_reminders ? 'Sí' : 'No'} · Salud: {l.consent_health_info ? 'Sí' : 'No'} · Comercial: {l.consent_commercial ? 'Sí' : 'No'}</div></div>
                    <Button size="sm" variant="outline" onClick={() => openPreview(`${BACKEND_URL}/api/lopd/${l.id}/pdf`, `lopd_${p.nif || p.id}.pdf`, `LOPD · ${fullName(p)}`)}><Eye className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="preop" className="mt-4">
          <SectionCard title="Pruebas preoperatorias">
            <PruebasPreopCard
              patientId={p.id}
              onPreview={({ url, filename, title }) => openPreview(url, filename, title)}
            />
          </SectionCard>

          {/* Sin «volantes» (2026-09-03): es la solicitud de pruebas. */}
          <SectionCard title={`Solicitud de analítica + ECG · ${data.preop.length}`} action={<Link to={`/preoperatorios?patient=${p.id}`} className="text-sm text-[#2E6E5E] hover:underline">+ Generar</Link>}>
            {data.preop.length === 0 ? <EmptyState icon={Stethoscope} title="Sin solicitudes" description="Genera la solicitud de pruebas." /> : (
              <div className="divide-y divide-border">
                {data.preop.map(po => (
                  <div key={po.id} className="py-3 flex items-center justify-between gap-3">
                    <div><div className="text-sm font-medium">Solicitud de Analítica + ECG</div><div className="text-xs text-muted-foreground">Generado: {formatDate(po.created_at)}</div></div>
                    <PdfLangButton
                      baseUrl={`${BACKEND_URL}/api/preop/${po.id}/pdf`}
                      filename={`preoperatorio_${p.nif || p.id}.pdf`}
                      title={`Solicitud de pruebas preoperatorias · ${fullName(p)}`}
                      onOpen={(x) => setPreview({ open: true, ...x })}
                    >
                      <Button size="sm" variant="outline" title="Ver preoperatorio (elige idioma)"><Eye className="w-4 h-4" /></Button>
                    </PdfLangButton>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="instructions" className="mt-4">
          <SectionCard title="Indicaciones día de la cirugía" action={<Link to={`/indicaciones?patient=${p.id}`} className="text-sm text-[#2E6E5E] hover:underline">+ Generar</Link>}>
            {(!data.instructions || data.instructions.length === 0) ? <EmptyState icon={ListChecks} title="Sin indicaciones" description="Se generan automáticamente al crear un presupuesto." /> : (
              <div className="divide-y divide-border">
                {data.instructions.map(it => (
                  <div key={it.id} className="py-3 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium">Indicaciones para el día de la cirugía {it.auto_generated_from_budget && <span className="ml-1 text-[10px] uppercase tracking-wider bg-[rgba(46,110,94,0.18)] text-[#13231E] px-1.5 py-0.5 rounded">auto</span>}</div>
                      <div className="text-xs text-muted-foreground">Generado: {formatDate(it.created_at)}{it.surgery_date ? ` · cirugía ${it.surgery_date}` : ''}</div>
                    </div>
                    <PdfLangButton
                      baseUrl={`${BACKEND_URL}/api/instructions/${it.id}/pdf`}
                      filename={`indicaciones_${p.nif || p.id}.pdf`}
                      title={`Indicaciones · ${fullName(p)}`}
                      onOpen={(x) => setPreview({ open: true, ...x })}
                    >
                      <Button size="sm" variant="outline" title="Ver indicaciones (elige idioma)"><Eye className="w-4 h-4" /></Button>
                    </PdfLangButton>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="consents" className="mt-4">
          <PatientConsentsTab
            patient={p}
            consents={data.consents}
            onPreview={openPreview}
            onReload={() => load(false)}
          />
        </TabsContent>

        <TabsContent value="informes" className="mt-4">
          <SectionCard
            title="Informes médicos"
            action={<Link to="/informes" className="text-sm text-[#2E6E5E] hover:underline">+ Nuevo</Link>}
          >
            {/* Ver, editar, borrar y mandar por WhatsApp (24/9/2026). */}
            <InformesPacienteTab
              patient={p}
              informes={informes}
              onPreview={openPreview}
              onReload={() => load(false)}
              puedeWhatsApp={gestion}
            />
          </SectionCard>
        </TabsContent>


        {/* Solo gestión, como su pestaña (D2, 22/9/2026): el contenido, sus
            botones y los dos cuadros de abajo. */}
        {gestion && (
        <TabsContent value="cobros" className="mt-4">
          <SectionCard
            title="Cobros realizados"
            action={(
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setCobroOpen(true)}
                className="text-[#2E6E5E] hover:text-[#1E4A3F]"
                data-testid="patient-registrar-cobro"
              >
                <Receipt className="w-4 h-4 mr-1" /> Registrar cobro
              </Button>
            )}
          >
            {data.cobros.length === 0 ? (
              <EmptyState icon={Receipt} title="Sin cobros" description="Cuando se registre un pago aparecerá aquí." />
            ) : (
              <div className="divide-y divide-border" data-testid="patient-cobros-list">
                {data.cobros.map(c => (
                  <div key={c.id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">{c.concept}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
                        <span>{formatDate(c.date)}</span>
                        <span className="inline-flex items-center gap-1 bg-muted/60 px-1.5 py-0.5 rounded">
                          <PaymentMethodIcon method={c.payment_method} /> {c.payment_method}
                        </span>
                        {c.source === 'quick_pay_agenda' && <Badge variant="secondary" className="text-[10px] bg-[#2E6E5E]/15 text-[#13231E] border-[#2E6E5E]/30">desde agenda</Badge>}
                        {c.source === 'devolucion' && <Badge variant="secondary" className="text-[10px] bg-red-50 text-red-800 border-red-200" data-testid={`cobro-devolucion-${c.id}`}>devolución</Badge>}
                        {c.created_by_name && (
                          <span className="inline-flex items-center gap-1" title="Quién registró este cobro" data-testid={`cobro-autor-${c.id}`}>
                            · Registrado por <strong className="text-[#13231E]/80 font-medium">{c.created_by_name}</strong>
                          </span>
                        )}
                        {/* Con IVA: desde el 24/9/2026 va DENTRO del importe;
                            los cobros de antes lo llevan encima y
                            así se guardaron. Se dice cuál es cuál. */}
                        {c.vat_applied && (esIvaIncluido(c)
                          ? <span className="text-[10px] bg-amber-50 text-amber-900 border border-amber-300 px-1.5 py-0.5 rounded" data-testid={`cobro-iva-incluido-${c.id}`}>IVA 21% incluido</span>
                          : <span className="text-[10px] bg-amber-50 text-amber-900 border border-amber-300 px-1.5 py-0.5 rounded">+IVA 21%</span>)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-semibold tabular-nums ${Number(c.total ?? c.amount_base ?? c.amount ?? 0) < 0 ? 'text-red-700' : 'text-[#13231E]'}`}>{formatEUR(c.total || c.amount_base || c.amount || 0)}</div>
                      {c.vat_applied && c.amount_base && (
                        <div className="text-[10px] text-muted-foreground tabular-nums" data-testid={`cobro-desglose-${c.id}`}>
                          base {formatEUR(c.amount_base)} · IVA {formatEUR(desgloseGuardado(c).iva)}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => setEditCobro(c)}
                        title="Corregir este cobro (queda registrado quién y qué cambia)"
                        data-testid={`cobro-editar-${c.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => borrarCobro(c)}
                        className="text-destructive" title="Borrar este cobro"
                        data-testid={`cobro-borrar-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </TabsContent>
        )}

        <TabsContent value="recetas" className="mt-4">
          {/* Emitir recetas SIN pasar por Quirófano (2026-08-19). */}
          <PatientRecetasTab patient={data.patient} onPreview={setPreview} />
        </TabsContent>

        <TabsContent value="postop" className="mt-4">
          <SectionCard
            title="Postoperatorios"
            action={
              <div className="flex items-center gap-3">
                {/* Mismo editor que en Quirófano (2026-08-27): al dar
                    el postoperatorio se quitan las pautas que no apliquen a
                    este paciente antes de generar el documento. */}
                <Button
                  size="sm"
                  className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                  onClick={() => setDarPostopOpen(true)}
                  data-testid="ficha-dar-postop"
                >
                  <BedDouble className="w-4 h-4 mr-1.5" /> Dar postoperatorio
                </Button>
                <Link to="/postoperatorios" className="text-sm text-[#2E6E5E] hover:underline">Gestionar plantillas</Link>
              </div>
            }
          >
            {postopDocs.length === 0 ? (
              <EmptyState icon={BedDouble} title="Sin postoperatorios" description="Cuando se genere uno aparecerá aquí." />
            ) : (
              <div className="divide-y divide-border">
                {postopDocs.map(d => (
                  <div key={d.id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{d.template_name || 'Postoperatorio'}</div>
                      <div className="text-xs text-muted-foreground">
                        Cirugía: {d.surgery_name || '—'} · Fecha: {d.surgery_date || '—'}
                        {d.custom_sections && d.custom_sections.length > 0 && <Badge variant="secondary" className="ml-2 text-[10px] bg-[#2E6E5E]/15 text-[#13231E] border-[#2E6E5E]/30">Editado</Badge>}
                      </div>
                    </div>
                    <PdfLangButton
                      baseUrl={`${BACKEND_URL}/api/postoperatorios/documents/${d.id}/pdf`}
                      filename={`postoperatorio_${p.nif || ''}.pdf`}
                      title={`Postoperatorio · ${fullName(p)}`}
                      onOpen={(x) => setPreview({ open: true, ...x })}
                    >
                      <Button size="sm" variant="outline" title="Ver postoperatorio (elige idioma)">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </PdfLangButton>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          {/* Phase 25.F — Hojas de Anestesia del paciente en el gestor documental */}
          <SectionCard
            title="Hojas de Anestesia"
            action={<Link to="/registro-quirurgico" className="text-sm text-[#2E6E5E] hover:underline">Ir a Registro QX</Link>}
          >
            {anesthesiaSheets.length === 0 ? (
              <EmptyState icon={FileText} title="Sin hojas de anestesia" description="Adjuntadas desde Registro de Quirófano." />
            ) : (
              <div className="divide-y divide-border" data-testid="patient-anesthesia-list">
                {anesthesiaSheets.map((s) => (
                  <div key={s.id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{s.file_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatDate(s.uploaded_at || s.created_at)} · {Math.round((s.size_bytes || 0) / 1024)} KB
                        {s.surgery_log_id && <span className="ml-2 text-[10px] bg-muted px-1.5 py-0.5 rounded">QX vinculado</span>}
                      </div>
                    </div>
                    <a
                      href={`${BACKEND_URL}/api/anesthesia-sheets/${s.id}/file`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="patient-anesthesia-open"
                    >
                      <Button size="sm" variant="outline">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </a>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </TabsContent>
      </Tabs>
      <PdfPreviewDialog open={preview.open} onOpenChange={(v) => setPreview(s => ({ ...s, open: v }))} url={preview.url} filename={preview.filename} title={preview.title} />
      {gestion && (
        <>
          <EditarCobroDialog
            cobro={editCobro}
            onOpenChange={(v) => { if (!v) setEditCobro(null); }}
            onSaved={() => load(false)}
          />
          <CobroQuickDialog
            open={cobroOpen}
            onOpenChange={setCobroOpen}
            prefillPatient={p}
            onSaved={() => load(false)}
          />
        </>
      )}
      {/* Editar los datos del paciente sin salir de su ficha (2026-08-25). */}
      <PatientFormDialog open={editOpen} onOpenChange={setEditOpen} initial={p} onSaved={() => load(false)} />
      {/* Sin cita: el cuadro le pregunta al servidor por el paciente y este
          mira su última intervención ya hecha (22/9/2026, M1 y M3). Es el
          camino de la intervención citada en consulta, que no pasa por el parte. */}
      <PostopQuickDialog
        open={darPostopOpen}
        onOpenChange={setDarPostopOpen}
        patient={{ patient_id: p.id, patient_name: fullName(p) }}
        surgeryDate={null}
        surgeryName=""
        onCreated={() => { setDarPostopOpen(false); load(false); }}
      />
      {/* Editar un presupuesto sin salir de la ficha (2026-08-26):
          'que pueda editarlo en ese menú, no solo en la pestaña presupuestos'. */}
      <NewBudgetDialog
        open={!!editBudget}
        onOpenChange={(v) => { if (!v) setEditBudget(null); }}
        budget={editBudget}
        onCreated={() => { setEditBudget(null); load(false); }}
      />
      <DarCitaDialog
        open={darCitaOpen}
        onOpenChange={setDarCitaOpen}
        patient={p}
        onCreated={() => setCitasRefreshKey((k) => k + 1)}
      />
    </div>
  );
}
